import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from "vue";
  import interactiveMasterplanApi from "@/services/api/interactiveMasterplanApi";
  import { PointOfInterestMetadata } from "@/types/InteractiveMasterplanInterface";

  interface BoundingBox {
    x: number;
    y: number;
    width: number;
    height: number;
  }

  type LocalPoi = PointOfInterestMetadata;

  type InteractionMode = "none" | "pan" | "move" | "resize";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'PointsOfInterestEditor',
  props: {
    svgFile: {},
    estateId: {},
    userId: {},
    initialPois: {}
  },
  emits: ["complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  // --- SVG viewer state ---
  const svgContainer = ref<HTMLElement | null>(null);
  const svgOverlay = ref<SVGSVGElement | null>(null);
  const svgContent = ref("");
  const svgViewBox = ref({ minX: 0, minY: 0, width: 800, height: 600 });
  const zoomLevel = ref(1);
  const panOffset = ref({ x: 0, y: 0 });
  const isDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const dragStartPan = ref({ x: 0, y: 0 });

  const overlayViewBox = computed(() => {
    const vb = svgViewBox.value;

    return `${vb.minX} ${vb.minY} ${vb.width} ${vb.height}`;
  });

  const svgTransformStyle = computed(() => ({
    transform: `translate(${panOffset.value.x}px, ${panOffset.value.y}px) scale(${zoomLevel.value})`,
    transformOrigin: "0 0",
    position: "absolute" as const,
    top: "0",
    left: "0",
  }));

  // --- POI state ---
  const localPois = ref<LocalPoi[]>(
    props.initialPois ? JSON.parse(JSON.stringify(props.initialPois)) : []
  );
  const selectedIndex = ref<number | null>(null);

  // --- Placing mode ---
  const isPlacingMode = ref(false);
  const ghostRect = ref<BoundingBox | null>(null);

  // --- Move/resize ---
  const interactionMode = ref<InteractionMode>("none");
  const activePoiIndex = ref<number | null>(null);
  const interactionStart = ref({
    mouseX: 0,
    mouseY: 0,
    origBox: null as BoundingBox | null,
  });

  // --- Image upload ---
  const uploadingIndex = ref<number | null>(null);
  const imageInputRefs = ref<(HTMLInputElement | null)[]>([]);

  const setImageInputRef = (el: unknown, index: number) => {
    if (el instanceof HTMLInputElement || el === null) {
      imageInputRefs.value[index] = el;
    }
  };

  const triggerImageUpload = (index: number) => {
    imageInputRefs.value[index]?.click();
  };

  const handleImageUpload = async (event: Event, index: number) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    uploadingIndex.value = index;

    try {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("userId", props.userId);
      formData.append("estateId", props.estateId);

      const response = await interactiveMasterplanApi.uploadPoiImage(formData);
      const data = response.data as { url: string; fileName: string };

      localPois.value[index].imageUrl = data.url;
      localPois.value[index].imageFileName = data.fileName;
    } catch (err) {
      console.error("Failed to upload POI image:", err);
      alert("Failed to upload image. Please try again.");
    } finally {
      uploadingIndex.value = null;
      input.value = "";
    }
  };

  const removeImage = (index: number) => {
    localPois.value[index].imageUrl = undefined;
    localPois.value[index].imageFileName = undefined;
  };

  const getPoiPinSize = (poi: LocalPoi) =>
    Math.min(poi.boundingBox.width, poi.boundingBox.height) * 0.75;

  // --- SVG loading ---
  onMounted(async () => {
    if (props.svgFile) {
      const text = await props.svgFile.text();

      svgContent.value = text;

      // Parse viewBox from SVG
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "image/svg+xml");
      const svgEl = doc.querySelector("svg");

      if (svgEl) {
        const vb = svgEl.getAttribute("viewBox");

        if (vb) {
          const parts = vb.split(/[\s,]+/).map(Number);

          if (parts.length === 4) {
            svgViewBox.value = {
              minX: parts[0],
              minY: parts[1],
              width: parts[2],
              height: parts[3],
            };
          }
        } else {
          const w = parseFloat(svgEl.getAttribute("width") || "800");
          const h = parseFloat(svgEl.getAttribute("height") || "600");

          svgViewBox.value = { minX: 0, minY: 0, width: w, height: h };
        }
      }
    }
  });

  const applyZoom = (newZoom: number, anchorX: number, anchorY: number) => {
    const ratio = newZoom / zoomLevel.value;

    panOffset.value = {
      x: anchorX - (anchorX - panOffset.value.x) * ratio,
      y: anchorY - (anchorY - panOffset.value.y) * ratio,
    };
    zoomLevel.value = newZoom;
  };

  const handleZoomIn = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.min(zoomLevel.value * 1.25, 10), cx, cy);
  };
  const handleZoomOut = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.max(zoomLevel.value / 1.25, 0.1), cx, cy);
  };
  const handleResetView = () => {
    zoomLevel.value = 1;
    panOffset.value = { x: 0, y: 0 };
  };

  const handleWheel = (e: WheelEvent) => {
    const container = svgContainer.value;

    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;

    applyZoom(
      Math.min(Math.max(zoomLevel.value * factor, 0.1), 10),
      mouseX,
      mouseY
    );
  };

  const screenToSvg = (
    screenX: number,
    screenY: number
  ): { x: number; y: number } => {
    if (!svgContainer.value) return { x: 0, y: 0 };

    const rect = svgContainer.value.getBoundingClientRect();
    const containerX = screenX - rect.left;
    const containerY = screenY - rect.top;
    const elX = (containerX - panOffset.value.x) / zoomLevel.value;
    const elY = (containerY - panOffset.value.y) / zoomLevel.value;
    const containerW = rect.width;
    const containerH = rect.height;
    const vb = svgViewBox.value;
    const meetScale = Math.min(containerW / vb.width, containerH / vb.height);
    const contentW = vb.width * meetScale;
    const contentH = vb.height * meetScale;
    const offsetX = (containerW - contentW) / 2;
    const offsetY = (containerH - contentH) / 2;
    const x = vb.minX + (elX - offsetX) / meetScale;
    const y = vb.minY + (elY - offsetY) / meetScale;

    return { x, y };
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (isPlacingMode.value) return;

    if (interactionMode.value !== "none") return;

    isDragging.value = true;
    interactionMode.value = "pan";
    dragStart.value = { x: e.clientX, y: e.clientY };
    dragStartPan.value = { ...panOffset.value };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isPlacingMode.value) {
      const pos = screenToSvg(e.clientX, e.clientY);
      const defaultW = svgViewBox.value.width * 0.08;
      const defaultH = svgViewBox.value.height * 0.06;

      ghostRect.value = {
        x: pos.x - defaultW / 2,
        y: pos.y - defaultH / 2,
        width: defaultW,
        height: defaultH,
      };
      return;
    }

    if (interactionMode.value === "pan" && isDragging.value) {
      panOffset.value = {
        x: dragStartPan.value.x + (e.clientX - dragStart.value.x),
        y: dragStartPan.value.y + (e.clientY - dragStart.value.y),
      };
      return;
    }

    if (interactionMode.value === "move" && activePoiIndex.value !== null) {
      const curr = screenToSvg(e.clientX, e.clientY);
      const start = screenToSvg(
        interactionStart.value.mouseX,
        interactionStart.value.mouseY
      );
      const orig = interactionStart.value.origBox;

      if (!orig) return;

      localPois.value[activePoiIndex.value].boundingBox = {
        ...orig,
        x: orig.x + (curr.x - start.x),
        y: orig.y + (curr.y - start.y),
      };
      return;
    }

    if (interactionMode.value === "resize" && activePoiIndex.value !== null) {
      const curr = screenToSvg(e.clientX, e.clientY);
      const orig = interactionStart.value.origBox;

      if (!orig) return;

      const newW = Math.max(30, curr.x - orig.x);
      const newH = Math.max(20, curr.y - orig.y);

      localPois.value[activePoiIndex.value].boundingBox = {
        ...orig,
        width: newW,
        height: newH,
      };
    }
  };

  const handleMouseUp = () => {
    isDragging.value = false;
    interactionMode.value = "none";
    activePoiIndex.value = null;
  };

  const handleMouseLeave = () => {
    if (isPlacingMode.value) {
      ghostRect.value = null;
    }

    handleMouseUp();
  };

  const handleContainerClick = (e: MouseEvent) => {
    if (!isPlacingMode.value) return;

    const pos = screenToSvg(e.clientX, e.clientY);
    const defaultW = svgViewBox.value.width * 0.08;
    const defaultH = svgViewBox.value.height * 0.06;
    const newPoi: LocalPoi = {
      title: "",
      description: "",
      boundingBox: {
        x: pos.x - defaultW / 2,
        y: pos.y - defaultH / 2,
        width: defaultW,
        height: defaultH,
      },
    };

    localPois.value.push(newPoi);
    selectedIndex.value = localPois.value.length - 1;
    isPlacingMode.value = false;
    ghostRect.value = null;
  };

  const togglePlacingMode = () => {
    isPlacingMode.value = !isPlacingMode.value;

    if (!isPlacingMode.value) ghostRect.value = null;
  };

  const selectPoi = (index: number) => {
    selectedIndex.value = index;
  };

  const deletePoi = (index: number) => {
    localPois.value.splice(index, 1);

    if (selectedIndex.value === index) selectedIndex.value = null;
    else if (selectedIndex.value !== null && selectedIndex.value > index)
      selectedIndex.value--;
  };

  const startMoving = (e: MouseEvent, index: number) => {
    e.preventDefault();
    interactionMode.value = "move";
    activePoiIndex.value = index;
    interactionStart.value = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      origBox: { ...localPois.value[index].boundingBox },
    };
  };

  const startResizing = (e: MouseEvent, index: number) => {
    e.preventDefault();
    interactionMode.value = "resize";
    activePoiIndex.value = index;
    interactionStart.value = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      origBox: { ...localPois.value[index].boundingBox },
    };
  };

  const handleComplete = () => {
    emit("complete", localPois.value);
  };

const __returned__ = { props, emit, svgContainer, svgOverlay, svgContent, svgViewBox, zoomLevel, panOffset, isDragging, dragStart, dragStartPan, overlayViewBox, svgTransformStyle, localPois, selectedIndex, isPlacingMode, ghostRect, interactionMode, activePoiIndex, interactionStart, uploadingIndex, imageInputRefs, setImageInputRef, triggerImageUpload, handleImageUpload, removeImage, getPoiPinSize, applyZoom, handleZoomIn, handleZoomOut, handleResetView, handleWheel, screenToSvg, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, handleContainerClick, togglePlacingMode, selectPoi, deletePoi, startMoving, startResizing, handleComplete, ref, computed, onMounted, get interactiveMasterplanApi() { return interactiveMasterplanApi }, get PointOfInterestMetadata() { return PointOfInterestMetadata } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})